'use client';

import { useEffect } from 'react';

/**
 * Options for the useLossPreventionWarning hook
 */
export interface UseLossPreventionWarningOptions {
  /**
   * Whether the user has unsaved content (files, images, URLs, text, or an existing bot)
   */
  hasUnsavedContent: boolean;
  /**
   * Whether the user is authenticated (authenticated users' work is saved)
   */
  isAuthenticated: boolean;
}

/**
 * Hook that shows a browser warning when unauthenticated users try to leave the page
 * with unsaved content.
 *
 * Uses the browser's native beforeunload event to trigger a confirmation dialog.
 * The warning is only shown for unauthenticated users who have created content.
 *
 * @param options - Configuration options for the hook
 * @returns void - This hook has no return value, it only manages side effects
 *
 * @example
 * ```tsx
 * useLossPreventionWarning({
 *   hasUnsavedContent: hasContent || textContent.length >= 10 || !!sharedBotId,
 *   isAuthenticated: !!user,
 * });
 * ```
 */
export function useLossPreventionWarning({
  hasUnsavedContent,
  isAuthenticated,
}: UseLossPreventionWarningOptions): void {
  useEffect(() => {
    // Only show warning for unauthenticated users with content
    if (!hasUnsavedContent || isAuthenticated) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Standard way to trigger browser's native dialog
      event.preventDefault();
      // Chrome requires returnValue to be set (deprecated but still needed)
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedContent, isAuthenticated]);
}
